import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { map, switchMap, tap } from 'rxjs/operators';
import { DialogData } from '../../../domain/common.model';
import { RoleCode } from '../../../domain/consts';
import { enrichParties, enrichPartiesProduct } from '../../../domain/document.model';
import { Offer, PolicyholderRequest } from '../../../domain/offer.domain';
import { Party } from '../../../domain/party.domain';
import {
    Driver,
    InsuredProduct,
    InsuredProductFrontier,
    InsuredProductMotor,
    InsuredProductProperty,
    MotorConfigurationStep,
    PropertyConfigurationStep
} from '../../../domain/product.domain';
import { FileService } from '../service/file.service';
import { GrowlService } from '../service/growl.service';
import { OfferService } from '../service/offer.service';
import { AsideAction, SidenavAction, SidenavSide, SidenavTemplate } from './aside.action';
import { DialogAction } from './dialog.action';
import { OfferAction } from './offer.action';
import { RouterAction } from './router.action';

export class OfferStateModel {
    offer: Offer;

    isPolicyholder: boolean;
    policyholder: PolicyholderRequest
    insured: Party;

    insuredProduct: InsuredProduct; // cession aside

    readonlyProperty: InsuredProductProperty;
    readonlyMotor: InsuredProductMotor;
    readonlyFrontier: InsuredProductFrontier;

    propertyProduct: InsuredProductProperty;
    propertyCalculated: InsuredProductProperty;
    motorProduct: InsuredProductMotor;
    motorCalculated: InsuredProductMotor;
}

@State<OfferStateModel>({
    name: 'offer',
    defaults: {
        offer: null,

        // party editor
        isPolicyholder: null,
        policyholder: null,
        insured: null,

        insuredProduct: null,

        // readonly
        readonlyProperty: null,
        readonlyMotor: null,
        readonlyFrontier: null,

        // property editor
        propertyProduct: null,
        propertyCalculated: null,

        // motor editor
        motorProduct: null,
        motorCalculated: null,
    }
})
@Injectable()
export class OfferState {

    constructor(private offerService: OfferService,
                private fileService: FileService,
                private growlService: GrowlService,
                private dialog: MatDialog,) {
    }

    @Selector()
    static getOffer(state: OfferStateModel) {
        return state.offer;
    }

    @Selector()
    static getIsPolicyholder(state: OfferStateModel) {
        return state.isPolicyholder;
    }

    @Selector()
    static getInsured(state: OfferStateModel) {
        return state.insured;
    }

    @Selector()
    static getPolicyholder(state: OfferStateModel) {
        return state.policyholder;
    }

    @Selector()
    static getPropertyProduct(state: OfferStateModel) {
        return state.propertyProduct;
    }

    @Selector()
    static getInsuredProduct(state: OfferStateModel) {
        return state.insuredProduct;
    }

    @Selector()
    static getPropertyCalculated(state: OfferStateModel) {
        return state.propertyCalculated;
    }

    @Selector()
    static getMotorProduct(state: OfferStateModel) {
        return state.motorProduct;
    }

    @Selector()
    static getMotorCalculated(state: OfferStateModel) {
        return state.motorCalculated;
    }

    @Selector()
    static getReadonlyProperty(state: OfferStateModel) {
        return state.readonlyProperty;
    }

    @Selector()
    static getReadonlyMotor(state: OfferStateModel) {
        return state.readonlyMotor;
    }

    @Selector()
    static getReadonlyFrontier(state: OfferStateModel) {
        return state.readonlyFrontier;
    }

    @Action(OfferAction.CreateCalculation)
    createCalculation(context: StateContext<OfferStateModel>, action: OfferAction.CreateCalculation) {
        return this.offerService.createCalculation(action.request)
            .pipe(map(result => {
                context.dispatch(new RouterAction.Navigate(['/calculation', result.id, 'card'], {replaceUrl: true}))
            }));
    }

    @Action(OfferAction.GetOne)
    getOne(context: StateContext<OfferStateModel>, action: OfferAction.GetOne) {
        return this.offerService.getOne(action.id)
            .pipe(switchMap(offer => context.dispatch(new OfferAction.PatchOfferState(offer))));
    }

    @Action(OfferAction.PatchOfferState)
    patchOfferState(context: StateContext<OfferStateModel>, {offer}: OfferAction.PatchOfferState) {
        enrichParties(offer);
        context.patchState({offer});
    }

    @Action(OfferAction.EditPolicyholder)
    editPolicyholder(context: StateContext<OfferStateModel>, action: OfferAction.EditPolicyholder) {
        context.patchState({isPolicyholder: true, insured: null, policyholder: action.policyholder})
        return context.dispatch(new AsideAction.Generic(SidenavSide.RIGHT, SidenavAction.OPEN, SidenavTemplate.PARTY_EDITOR));
    }

    @Action(OfferAction.SavePolicyholder)
    savePolicyholder(context: StateContext<OfferStateModel>, action: OfferAction.SavePolicyholder) {
        return this.offerService.savePolicyholder(action.id, action.policyholder)
            .pipe(
                switchMap(offer => context.dispatch(new OfferAction.PatchOfferState(offer))),
                tap(() => context.dispatch(AsideAction.CloseRight()))
            );
    }

    @Action(OfferAction.EditInsured)
    editInsured(context: StateContext<OfferStateModel>, action: OfferAction.EditInsured) {
        context.patchState({isPolicyholder: false, insured: action.party, policyholder: null})
        return context.dispatch(new AsideAction.Generic(SidenavSide.RIGHT, SidenavAction.OPEN, SidenavTemplate.PARTY_EDITOR));
    }

    @Action(OfferAction.SaveInsured)
    saveInsured(context: StateContext<OfferStateModel>, action: OfferAction.SaveInsured) {
        return this.offerService.saveInsured(action.id, action.party)
            .pipe(
                switchMap(offer => context.dispatch(new OfferAction.PatchOfferState(offer))),
                tap(() => context.dispatch(AsideAction.CloseRight()))
            );
    }

    @Action(OfferAction.DeleteInsured)
    deleteInsured(context: StateContext<OfferStateModel>, action: OfferAction.DeleteInsured) {
        const data = new DialogData();
        data.titleLabel = 'LABEL.CONFIRMATION';
        data.descriptionLabel = 'MODAL.DELETE_INSURED_CONFIRMATION';
        data.onConfirm = () => {
            return this.offerService.deleteInsured(action.id, action.party)
                .pipe(switchMap(offer => {
                    this.growlService.showInfoGrowl('GROWL.INSURED_DELETED');
                    return context.dispatch(new OfferAction.PatchOfferState(offer));
                }));
        };
        return context.dispatch(new DialogAction.OpenGeneric(data));
    }

    @Action(OfferAction.DeleteProduct)
    deleteProduct(context: StateContext<OfferStateModel>, action: OfferAction.DeleteProduct) {
        const data = new DialogData();
        data.titleLabel = 'LABEL.CONFIRMATION';
        data.descriptionLabel = 'MODAL.DELETE_PRODUCT_CONFIRMATION';
        data.onConfirm = () => {
            return this.offerService.deleteProduct(action.id, action.productId)
                .pipe(switchMap(offer => {
                    this.growlService.showInfoGrowl('GROWL.PRODUCT_DELETED');
                    return context.dispatch(new OfferAction.PatchOfferState(offer));
                }));
        };
        return context.dispatch(new DialogAction.OpenGeneric(data));
    }

    @Action(OfferAction.CreateProductProperty)
    createProductProperty(context: StateContext<OfferStateModel>, action: OfferAction.CreateProductProperty) {
        return this.offerService.createProductProperty(action.id)
            .pipe(map(propertyProduct => {
                propertyProduct.configuration.configurationStep = PropertyConfigurationStep.BASIC;
                context.patchState({propertyProduct});
                return context.dispatch(new AsideAction.Generic(SidenavSide.RIGHT, SidenavAction.OPEN, SidenavTemplate.OFFER_PROPERTY_CONFIG));
            }));
    }

    @Action(OfferAction.ValidationPropertyBasic)
    validationPropertyBasic(context: StateContext<OfferStateModel>, action: OfferAction.ValidationPropertyBasic) {
        return this.offerService.validateProperty(action.offerId, action.product)
            .pipe(map(propertyProduct => {
                propertyProduct.configuration.configurationStep = PropertyConfigurationStep.DETAILS;
                const patched = propertyProduct.configuration.calculated
                    ? {propertyProduct, propertyCalculated: propertyProduct}
                    : {propertyProduct, propertyCalculated: null};
                return context.patchState(patched);
            }));
    }

    @Action(OfferAction.CalculateProperty)
    calculateProperty(context: StateContext<OfferStateModel>, action: OfferAction.CalculateProperty) {
        return this.offerService.calculateProperty(action.offerId, action.product)
            .pipe(map(propertyProduct => {
                return context.patchState({propertyProduct, propertyCalculated: propertyProduct});
            }));
    }

    @Action(OfferAction.SaveProperty)
    saveProperty(context: StateContext<OfferStateModel>, action: OfferAction.SaveProperty) {
        return this.offerService.saveProperty(action.offerId, action.product)
            .pipe(
                switchMap(offer => context.dispatch(new OfferAction.PatchOfferState(offer))),
                tap(() => context.dispatch(AsideAction.CloseRight()))
            );
    }

    @Action(OfferAction.CreateProductMotor)
    createProductMotor(context: StateContext<OfferStateModel>, action: OfferAction.CreateProductMotor) {
        return this.offerService.createProductMotor(action.id)
            .pipe(map(motorProduct => {
                const offer = context.getState().offer;
                enrichPartiesProduct(offer, motorProduct);

                motorProduct.configuration.configurationStep = MotorConfigurationStep.BASIC;
                const driverList = offer.partyRoleList
                    .filter(r => r.role === RoleCode.INSURED_PERSON)
                    .map(r => {
                        const driver = new Driver();
                        driver.partyId = r.partyId;
                        const old = motorProduct.configuration.driverList.find(d => d.partyId === r.partyId);
                        if (old) {
                            driver.licenseDate = old.licenseDate;
                            driver.noLicense = old.noLicense;
                            driver.insuranceClaimHistory = old.insuranceClaimHistory;
                        }
                        return driver;
                    });
                motorProduct.configuration.driverList = driverList;

                context.patchState({motorProduct, motorCalculated: null});
                return context.dispatch(new AsideAction.Generic(SidenavSide.RIGHT, SidenavAction.OPEN, SidenavTemplate.OFFER_MOTOR_CONFIG));
            }));
    }

    @Action(OfferAction.ValidationMotorBasic)
    validationMotorBasic(context: StateContext<OfferStateModel>, action: OfferAction.ValidationMotorBasic) {
        return this.offerService.validateMotorBasic(action.offerId, action.product)
            .pipe(map(motorProduct => {
                const offer = context.getState().offer;
                enrichPartiesProduct(offer, motorProduct);
                if (motorProduct.configuration.autocasco.sumOfInsurance === null) {
                    motorProduct.configuration.autocasco.sumOfInsurance = motorProduct.configuration.vehicleDefinition.vehicleValue;
                }
                if (motorProduct.configuration.showUfgConfigurationScreen) {
                    motorProduct.configuration.configurationStep = MotorConfigurationStep.CLAIMS;
                } else {
                    motorProduct.configuration.configurationStep = MotorConfigurationStep.DETAILS;
                }

                const patched = motorProduct.configuration.calculated
                    ? {motorProduct, motorCalculated: motorProduct}
                    : {motorProduct, motorCalculated: null};
                return context.patchState(patched);
            }));
    }

    @Action(OfferAction.ValidationMotorClaims)
    validationMotorClaims(context: StateContext<OfferStateModel>, action: OfferAction.ValidationMotorClaims) {
        return this.offerService.validateMotorClaims(action.offerId, action.product)
            .pipe(map(motorProduct => {
                const offer = context.getState().offer;
                enrichPartiesProduct(offer, motorProduct);
                if (motorProduct.configuration.autocasco.sumOfInsurance === null) {
                    motorProduct.configuration.autocasco.sumOfInsurance = motorProduct.configuration.vehicleDefinition.vehicleValue;
                }

                motorProduct.configuration.configurationStep = MotorConfigurationStep.DETAILS;

                const patched = motorProduct.configuration.calculated
                    ? {motorProduct, motorCalculated: motorProduct}
                    : {motorProduct, motorCalculated: null};
                return context.patchState(patched);
            }));
    }

    @Action(OfferAction.CalculateMotor)
    calculateMotor(context: StateContext<OfferStateModel>, action: OfferAction.CalculateMotor) {
        return this.offerService.calculateMotor(action.offerId, action.product)
            .pipe(map(motorProduct => {
                const offer = context.getState().offer;
                enrichPartiesProduct(offer, motorProduct);
                return context.patchState({motorProduct, motorCalculated: motorProduct});
            }));
    }

    @Action(OfferAction.SaveMotor)
    saveMotor(context: StateContext<OfferStateModel>, action: OfferAction.SaveMotor) {
        return this.offerService.saveMotor(action.offerId, action.product)
            .pipe(
                switchMap(offer => context.dispatch(new OfferAction.PatchOfferState(offer))),
                tap(() => context.dispatch(AsideAction.CloseRight()))
            );
    }

    @Action(OfferAction.SaveLocation)
    saveLocation(context: StateContext<OfferStateModel>, action: OfferAction.SaveLocation) {
        return this.offerService.saveLocation(action.calculationId, action.location)
            .pipe(
                switchMap(offer => context.dispatch(new OfferAction.PatchOfferState(offer))),
                tap(() => context.dispatch(new DialogAction.CloseAll())),
            );
    }

    @Action(OfferAction.SaveContactData)
    saveContactData(context: StateContext<OfferStateModel>, action: OfferAction.SaveContactData) {
        return this.offerService.saveContactData(action.calculationId, action.data)
            .pipe(
                switchMap(offer => context.dispatch(new OfferAction.PatchOfferState(offer))),
                tap(() => context.dispatch(new DialogAction.CloseAll())),
            );
    }

    @Action(OfferAction.SaveEmailDataForIOffer)
    saveEmailDataForIOffer(context: StateContext<OfferStateModel>, action: OfferAction.SaveEmailDataForIOffer) {
        return this.offerService.saveEmailData(action.calculationId, action.data)
            .pipe(
                tap(() => context.dispatch(new DialogAction.CloseAll())),
                switchMap(offer => {
                    context.dispatch(new OfferAction.PatchOfferState(offer));
                    return context.dispatch(new OfferAction.CreateIOffer(offer.id));
                }),
            );
    }

    @Action(OfferAction.DeleteContactData)
    deleteContactData(context: StateContext<OfferStateModel>, action: OfferAction.DeleteContactData) {

        const data = new DialogData();
        data.titleLabel = 'LABEL.CONFIRMATION';
        data.descriptionLabel = 'MODAL.DELETE_CONTACT_DATA';
        data.onConfirm = () => {
            return context.dispatch(new OfferAction.SaveContactData(action.calculationId, {
                email: null,
                phoneNumber: null,
                phonePrefix: '+48',
            }));
        };
        return context.dispatch(new DialogAction.OpenGeneric(data));
    }

    @Action(OfferAction.OpenCessionAside)
    openCessionAside(context: StateContext<OfferStateModel>, action: OfferAction.OpenCessionAside) {
        context.patchState({insuredProduct: action.insuredProduct});
        return context.dispatch(new AsideAction.Generic(SidenavSide.RIGHT, SidenavAction.OPEN, SidenavTemplate.CESSION_EDITOR));
    }

    @Action(OfferAction.SaveCession)
    saveCession(context: StateContext<OfferStateModel>, action: OfferAction.SaveCession) {
        return this.offerService.saveCession(action.calculationId, action.productId, action.data)
            .pipe(
                switchMap(offer => context.dispatch(new OfferAction.PatchOfferState(offer))),
                tap(() => context.dispatch(new AsideAction.CloseAll())),
            );
    }

    @Action(OfferAction.DeleteCession)
    deleteCession(context: StateContext<OfferStateModel>, action: OfferAction.DeleteCession) {
        const data = new DialogData();
        data.titleLabel = 'LABEL.CONFIRMATION';
        data.descriptionLabel = 'MODAL.DELETE_CESSION_CONFIRMATION';
        data.onConfirm = () => {
            return this.offerService.deleteCession(action.calculationId, action.productId)
                .pipe(switchMap(offer => {
                    this.growlService.showInfoGrowl('GROWL.CESSION_DELETED');
                    return context.dispatch(new OfferAction.PatchOfferState(offer));
                }));
        };
        return context.dispatch(new DialogAction.OpenGeneric(data));
    }

    @Action(OfferAction.CreateOffer)
    createOffer(context: StateContext<OfferStateModel>, action: OfferAction.CreateOffer) {
        return this.offerService.createOffer(action.offer)
            .pipe(map(result => {
                context.dispatch(new RouterAction.Navigate(['/calculation', result.id, 'offer']))
            }));
    }

    @Action(OfferAction.BackToCalculation)
    backToCalculation(context: StateContext<OfferStateModel>, action: OfferAction.BackToCalculation) {
        const data = new DialogData();
        data.titleLabel = 'LABEL.CONFIRMATION';
        data.descriptionLabel = 'OFFER.BACK_TO_CALCULATION_CONFIRMATION';
        data.onConfirm = () => {
            return this.offerService.backToCalculation(action.offerId)
                .pipe(map(result => {
                    context.dispatch(new RouterAction.Navigate(['/calculation', result.id, 'card']))
                }));
        };
        return context.dispatch(new DialogAction.OpenGeneric(data));
    }

    @Action(OfferAction.ReadonlyMotor)
    readonlyMotor(context: StateContext<OfferStateModel>, action: OfferAction.ReadonlyMotor) {
        context.patchState({readonlyMotor: action.product});
        return context.dispatch(new AsideAction.Generic(SidenavSide.RIGHT, SidenavAction.OPEN, SidenavTemplate.OFFER_MOTOR_READONLY));
    }

    @Action(OfferAction.ReadonlyProperty)
    readonlyProperty(context: StateContext<OfferStateModel>, action: OfferAction.ReadonlyProperty) {
        context.patchState({readonlyProperty: action.product});
        return context.dispatch(new AsideAction.Generic(SidenavSide.RIGHT, SidenavAction.OPEN, SidenavTemplate.OFFER_PROPERTY_READONLY));
    }

    @Action(OfferAction.ReadonlyFrontier)
    readonlyFrontier(context: StateContext<OfferStateModel>, action: OfferAction.ReadonlyFrontier) {
        context.patchState({readonlyFrontier: action.product});
        return context.dispatch(new AsideAction.Generic(SidenavSide.RIGHT, SidenavAction.OPEN, SidenavTemplate.OFFER_FRONTIER_READONLY));
    }

    @Action(OfferAction.IssueFromCalculation)
    issueFromCalculation(context: StateContext<OfferStateModel>, action: OfferAction.CreateOffer) {
        const data = new DialogData();
        data.titleLabel = 'POLICY_DIALOG.HEADER';
        data.descriptionLabel = 'CONFIRMATION.ISSUE_POLICY_FROM_CALCULATION';
        data.onConfirm = () => {
            return this.offerService.createOffer(action.offer)
                .pipe(switchMap(result => context.dispatch(new OfferAction.IssueReal(result.id))));
        };
        return context.dispatch(new DialogAction.OpenGeneric(data));
    }

    @Action(OfferAction.Issue)
    issue(context: StateContext<OfferStateModel>, action: OfferAction.Issue) {
        const data = new DialogData();
        data.titleLabel = 'POLICY_DIALOG.HEADER';
        data.descriptionLabel = 'CONFIRMATION.ISSUE_POLICY_FROM_OFFER';
        data.onConfirm = () => {
            return context.dispatch(new OfferAction.IssueReal(action.id));
        };
        return context.dispatch(new DialogAction.OpenGeneric(data));
    }

    @Action(OfferAction.IssueReal)
    issueReal(context: StateContext<OfferStateModel>, action: OfferAction.IssueReal) {
        return this.offerService.issue(action.id)
            .subscribe(policies => context.dispatch(new RouterAction.Navigate(['/policy', policies[0].offerId])));
    }

    @Action(OfferAction.PrintoutCalculation)
    printoutCalculation(context: StateContext<OfferStateModel>, action: OfferAction.PrintoutCalculation) {
        return this.offerService.printoutCalculation(action.id, action.productId, action.all)
            .pipe(tap(file => {
                this.fileService.getFileByToken(file.token);
            }));
    }

    @Action(OfferAction.PrintoutOffer)
    printoutOffer(context: StateContext<OfferStateModel>, action: OfferAction.PrintoutOffer) {
        return this.offerService.printoutOffer(action.id, action.productId, action.all)
            .pipe(tap(file => {
                this.fileService.getFileByToken(file.token);
            }));
    }

    @Action(OfferAction.CreateIOffer)
    createIOffer(context: StateContext<OfferStateModel>, action: OfferAction.CreateIOffer) {
        const data = new DialogData();
        data.titleLabel = 'LABEL.CONFIRMATION';
        data.descriptionLabel = 'MODAL.SEND_OFFER';
        data.onConfirm = () => {
            return this.offerService.createIOffer(action.id)
                .pipe(switchMap(offer => context.dispatch(new OfferAction.PatchOfferState(offer))));
        };
        return context.dispatch(new DialogAction.OpenGeneric(data));
    }

}
